import React from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

export interface MediaChipProps {
    label: string;
    /** A picture for the thumbnail. */
    thumbnailUrl?: string;
    /** A video to show a frame of, when there is no picture. */
    videoUrl?: string;
    /** Shown when there is neither. */
    icon?: React.ReactNode;
    /** Working on it: a spinner in place of the thumbnail. */
    busy?: boolean;
    /** Clicking the chip — usually opens the picture large. */
    onOpen?: () => void;
    openLabel?: string;
    onRemove?: () => void;
    removeLabel?: string;
    size?: 'sm' | 'md';
    className?: string;
}

/**
 * A thing attached to something — a product, a reference, a file — as a small
 * chip with its picture and its name.
 *
 * One look wherever attachments appear (the composer, a sent message, a
 * shot's products), so a product reads as the same object everywhere and the
 * picture, not the name, is what the eye finds first.
 */
export const MediaChip: React.FC<MediaChipProps> = ({
    label, thumbnailUrl, videoUrl, icon, busy, onOpen, openLabel, onRemove, removeLabel, size = 'md', className,
}) => {
    const thumb = size === 'sm' ? 'size-5 rounded-[5px]' : 'size-6 rounded-md';
    const media = busy
        ? <span className={cn(thumb, 'flex shrink-0 items-center justify-center bg-muted')}><Spinner className="size-3" /></span>
        : thumbnailUrl
            ? <img src={thumbnailUrl} alt="" className={cn(thumb, 'shrink-0 bg-muted object-cover')} />
            : videoUrl
                ? <video src={videoUrl} muted playsInline preload="metadata" className={cn(thumb, 'shrink-0 bg-black object-cover')} />
                : <span className={cn(thumb, 'flex shrink-0 items-center justify-center bg-muted text-muted-foreground [&_svg]:size-3.5')}>{icon}</span>;

    return (
        <span className={cn(
            'inline-flex max-w-full items-center gap-1.5 rounded-lg border bg-background text-foreground',
            size === 'sm' ? 'h-7 pl-1 pr-1.5 text-[11px]' : 'h-8 pl-1 pr-2 text-xs',
            className,
        )}>
            <button
                type="button"
                disabled={!onOpen}
                onClick={onOpen}
                title={onOpen ? openLabel : undefined}
                className="inline-flex min-w-0 items-center gap-1.5 enabled:cursor-pointer disabled:cursor-default [&:enabled:hover>span:last-child]:underline [&:enabled:hover>span:last-child]:underline-offset-2"
            >
                {media}
                <span className="max-w-[180px] truncate">{label}</span>
            </button>
            {onRemove && (
                <button type="button" onClick={onRemove} aria-label={removeLabel} className="-mr-0.5 flex size-4 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                    <X className="size-3" />
                </button>
            )}
        </span>
    );
};
